import type { Product } from './types/product';

const API_BASE = '/api';

async function apiGet<T>(endpoint: string, params?: Record<string, string>): Promise<T> {
  const url = new URL(API_BASE + endpoint, window.location.origin);
  if (params) {
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        url.searchParams.set(key, value);
      }
    });
  }

  const res = await fetch(url.toString(), { 
    credentials: 'include',
    headers: { 'Accept': 'application/json' }
  });

  if (!res.ok) throw new Error(`API Fehler: ${res.status}`);

  const json = await res.json();
  if (json && json.error) throw new Error(json.error);

  if (json && typeof json === 'object' && 'result' in json && json.result && typeof json.result === 'object' && 'data' in json.result) {
    return json.result.data as T;
  }

  return json as T;
}

async function apiPost<T>(endpoint: string, body: unknown): Promise<T> {
  const res = await fetch(API_BASE + endpoint, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
    credentials: 'include',
  });

  if (res.status === 401) throw new Error('Nicht autorisiert oder falsches Passwort');
  if (!res.ok) throw new Error('API Fehler bei POST');

  const json = await res.json();
  if (json && json.error) throw new Error(json.error);

  if (json && typeof json === 'object' && 'result' in json && json.result && typeof json.result === 'object') {
    return json.result as T;
  }

  return json as T;
}

// ═══ FIX: PUT über POST + Header ═══
async function apiPut<T>(endpoint: string, body: unknown): Promise<T> {
  const res = await fetch(API_BASE + endpoint, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-HTTP-Method-Override': 'PUT',
    },
    body: JSON.stringify(body),
    credentials: 'include',
  });

  if (!res.ok) throw new Error('API Fehler bei PUT');
  const json = await res.json();
  if (json && json.error) throw new Error(json.error);

  if (json && typeof json === 'object' && 'result' in json && json.result && typeof json.result === 'object') {
    return json.result as T;
  }

  return json as T;
}

// ═══ FIX: DELETE über POST + Header ═══
async function apiDelete<T>(endpoint: string, body?: unknown): Promise<T> {
  const res = await fetch(API_BASE + endpoint, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-HTTP-Method-Override': 'DELETE',
    },
    body: body ? JSON.stringify(body) : undefined,
    credentials: 'include',
  });

  if (!res.ok) throw new Error('API Fehler bei DELETE');
  const json = await res.json();
  if (json && json.error) throw new Error(json.error);

  if (json && typeof json === 'object' && 'result' in json && json.result && typeof json.result === 'object') {
    return json.result as T;
  }

  return json as T;
}

export const authApi = {
  check: (): Promise<{ authenticated: boolean }> => 
    apiGet<{ authenticated: boolean }>('/auth.php'),
    
  login: (password: string): Promise<{ success: boolean }> => 
    apiPost<{ success: boolean }>('/auth.php', { action: 'login', password }),
    
  logout: (): Promise<{ success: boolean }> => 
    apiPost<{ success: boolean }>('/auth.php', { action: 'logout' }),
};

export const productApi = {
  list: (category?: string): Promise<Product[]> => 
    apiGet<Product[]>('/products.php', category && category !== 'all' ? { category } : {}),
  
  get: (slug: string): Promise<Product> => 
    apiGet<Product>('/products.php', { slug }),
  
  create: (data: Partial<Product>): Promise<Product> => 
    apiPost<Product>('/products.php', data),
  
  update: (data: Partial<Product>): Promise<{ success: boolean }> => 
    apiPut<{ success: boolean }>('/products.php', data),
  
  // ═══ FIX: DELETE über apiDelete ═══
  delete: (id: number): Promise<{ success: boolean }> =>
    apiDelete<{ success: boolean }>('/products.php', { id }),
};

export const settingsApi = {
  getShopStatus: (): Promise<{ enabled: boolean }> => 
    apiGet<{ enabled: boolean }>('/settings.php'),
    
  updateShopStatus: (enabled: boolean): Promise<{ success: boolean }> => 
    apiPut<{ success: boolean }>('/settings.php', { enabled }),
};

export const voucherApi = {
  check: (code: string): Promise<{ code: string; amount: number; type: 'fixed' | 'percent' }> =>
    apiGet('/vouchers.php', { code }),

  list: (): Promise<any[]> =>
    apiGet('/vouchers.php'),

  // ═══ FIX: DELETE über apiDelete ═══
  delete: (id: number): Promise<{ success: boolean }> =>
    apiDelete<{ success: boolean }>('/vouchers.php', { id }),
};

export const orderApi = {
  create: (data: { email: string; items: any[] }): Promise<{ success: boolean; tokens: any[] }> =>
    apiPost<{ success: boolean; tokens: any[] }>('/order.php', data),
};

export const adminOrderApi = {
  list: (): Promise<any[]> =>
    apiGet('/orders.php'),

  updateStatus: (id: number, status: string): Promise<{ success: boolean }> =>
    apiPut<{ success: boolean }>('/orders.php', { id, status }),
};